// src/page/StudentRegistration.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Typography,
    Alert,
    Badge,
    Paper,
    Divider,
    useMediaQuery,
    useTheme,
    InputAdornment,
    Chip,
    Stepper,
    Step,
    StepLabel,
    Fade,
    Zoom,
    Container,
    Card,
    CardContent,
    CircularProgress,
    LinearProgress,
    FormHelperText,
    IconButton,
    Grid,
    Autocomplete,
    Avatar,
    FormControlLabel,
    Switch,
    Collapse
} from '@mui/material';
import { styled, alpha as muiAlpha } from '@mui/material/styles';
import {
    Person,
    Email,
    Phone,
    School,
    Flag,
    Lock,
    Visibility,
    VisibilityOff,
    CheckCircle,
    Cancel,
    Login,
    ArrowBack,
    Send,
    Badge as BadgeIcon,
    ArrowBackIos,
    AccountCircle,
    Celebration,
    CheckCircleOutlineRounded,
    Domain,
    Email as EmailIcon,
    LocationCity
} from '@mui/icons-material';
import useCustomToast from '../hooks/useToast';
import { authService } from '../apiServices/authService';
import { APIUniversityNames, APIUniversitySchools } from '../apiServices/APIs';
import { logoIcon } from '../assets';

// Validation constants
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT_REGEX = /^\d{10}$/;

// Styled Components
const ModernCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
    backdropFilter: 'blur(10px)',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(3),
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
}));

const LogoImage = styled('img')(({ theme }) => ({
    height: 80,
    width: 80,
    borderRadius: 12,
    objectFit: 'contain',
    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
}));

const ActionButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: 'bold',
    py: 1.2,
    borderRadius: 2,
    transition: 'all 0.3s ease',
    fontSize: '0.95rem',
    '&:hover': {
        transform: 'translateY(-2px)',
    }
}));

// Success Page Component
const SuccessPage = ({ userData, onRedirect }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [countdown, setCountdown] = useState(10);

    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    onRedirect();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [onRedirect]);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `${theme.palette.common.auth}`,
                padding: isMobile ? 2 : 3,
            }}
        >
            <Container maxWidth="md">
                <Zoom in={true} timeout={800}>
                    <ModernCard elevation={0}>
                        <CardContent sx={{ p: isMobile ? 3 : 5 }}>
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <Zoom in={true} timeout={1000}>
                                    <Avatar
                                        sx={{
                                            width: isMobile ? 80 : 100,
                                            height: isMobile ? 80 : 100,
                                            margin: '0 auto',
                                            background: 'linear-gradient(135deg, #10B981, #059669)',
                                            animation: 'pulse 2s infinite',
                                            '@keyframes pulse': {
                                                '0%': {
                                                    transform: 'scale(0.95)',
                                                    boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.7)',
                                                },
                                                '70%': {
                                                    transform: 'scale(1)',
                                                    boxShadow: '0 0 0 10px rgba(16, 185, 129, 0)',
                                                },
                                                '100%': {
                                                    transform: 'scale(0.95)',
                                                    boxShadow: '0 0 0 0 rgba(16, 185, 129, 0)',
                                                },
                                            },
                                        }}
                                    >
                                        <CheckCircleOutlineRounded sx={{ fontSize: isMobile ? 50 : 60 }} />
                                    </Avatar>
                                </Zoom>

                                <Typography
                                    variant="h4"
                                    sx={{
                                        mt: 3,
                                        mb: 2,
                                        fontWeight: 'bold',
                                        background: 'linear-gradient(135deg, #10B981, #059669)',
                                        backgroundClip: 'text',
                                        textFillColor: 'transparent',
                                        fontSize: isMobile ? '1.5rem' : '2rem',
                                    }}
                                >
                                    Registration Successful!
                                </Typography>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                                    Welcome to SJC ScholarFlow
                                </Typography>

                                <Chip
                                    icon={<Celebration />}
                                    label="Account Activated Successfully"
                                    color="success"
                                    sx={{ mt: 1, mb: 2 }}
                                />
                            </Box>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: isMobile ? 2 : 3,
                                    mb: 3,
                                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                                    borderRadius: 3,
                                    border: '1px solid #86efac',
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        fontWeight: 'bold',
                                        color: '#166534',
                                    }}
                                >
                                    <AccountCircle />
                                    Account Details
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Person color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Full Name
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.full_name || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <EmailIcon color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Email Address
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.email || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Phone color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Contact Number
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.contact_phone || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <School color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    University
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.university_name || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    {userData?.school_name && (
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                <Domain color="action" fontSize="small" />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        School/Department
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {userData?.school_name}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    )}

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Flag color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Nationality
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.nationality || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <BadgeIcon color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Title
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.title || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: isMobile ? 2 : 3,
                                    mb: 3,
                                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                    borderRadius: 3,
                                    border: '1px solid #93c5fd',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <CheckCircle color="success" />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e40af', mb: 1 }}>
                                            <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Account Activated
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#1e3a8a', lineHeight: 1.6 }}>
                                            Your account has been automatically activated. You can now log in and start using 
                                            the SJC ScholarFlow platform immediately.
                                            <br />
                                            <strong>No email verification required.</strong>
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>

                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Redirecting to login page in {countdown} seconds...
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={(countdown / 10) * 100}
                                    sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        mb: 2,
                                        backgroundColor: '#e5e7eb',
                                        '& .MuiLinearProgress-bar': {
                                            background: 'linear-gradient(90deg, #10B981, #059669)',
                                            borderRadius: 3,
                                        },
                                    }}
                                />
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        onClick={onRedirect}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: 2,
                                            background: 'linear-gradient(135deg, #10B981, #059669)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #059669, #047857)',
                                            },
                                        }}
                                    >
                                        Go to Login Now
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </ModernCard>
                </Zoom>
            </Container>
        </Box>
    );
};

const StudentRegistration = () => {
    const navigate = useNavigate();
    const isSubmittingRef = useRef(false);
    const showToast = useCustomToast();
    const [error, setError] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Form state
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        contact_phone: '',
        title: '',
        nationality: '',
        university_id: '',
        university_name: '',
        school_id: '',
        school_name: '',
        has_schools: false,
        gender: '',
        password: '',
        password_confirmation: ''
    });

    // UI state
    const [universities, setUniversities] = useState([]);
    const [universitySearchInput, setUniversitySearchInput] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessPage, setShowSuccessPage] = useState(false);
    const [registeredUserData, setRegisteredUserData] = useState(null);
    const [selectedUniversitySchools, setSelectedUniversitySchools] = useState([]);
    const [loadingSchools, setLoadingSchools] = useState(false);

    // Validation state
    const [errors, setErrors] = useState({
        full_name: '',
        email: '',
        contact_phone: '',
        title: '',
        nationality: '',
        university_id: '',
        school_id: '',
        gender: '',
        password: '',
        password_confirmation: '',
        form: ''
    });

    // Step validation state
    const [stepValidations, setStepValidations] = useState({
        0: false, // Personal Info
        1: false, // Academic Details
        2: false  // Security
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchUniversities();
    }, []);

    useEffect(() => {
        validateCurrentStep();
    }, [formData, activeStep]);

    // Load schools when university is selected and has_schools is true
    useEffect(() => {
        if (formData.has_schools && formData.university_id) {
            loadUniversitySchools(formData.university_id);
        } else {
            setSelectedUniversitySchools([]);
            if (!formData.has_schools) {
                setFormData(prev => ({ ...prev, school_id: '', school_name: '' }));
            }
        }
    }, [formData.has_schools, formData.university_id]);

    const validateCurrentStep = () => {
        let isValid = true;
        const newStepValidations = { ...stepValidations };
        const newErrors = { ...errors };

        switch (activeStep) {
            case 0:
                newErrors.full_name = '';
                newErrors.contact_phone = '';
                newErrors.title = '';
                newErrors.nationality = '';
                newErrors.gender = '';

                if (!formData.full_name.trim()) {
                    newErrors.full_name = 'Full name is required';
                    isValid = false;
                }

                if (!formData.contact_phone.trim()) {
                    newErrors.contact_phone = 'Contact number is required';
                    isValid = false;
                } else if (!CONTACT_REGEX.test(formData.contact_phone)) {
                    newErrors.contact_phone = 'Contact number must be exactly 10 digits';
                    isValid = false;
                }

                if (!formData.title) {
                    newErrors.title = 'Title is required';
                    isValid = false;
                }

                if (!formData.nationality.trim()) {
                    newErrors.nationality = 'Nationality is required';
                    isValid = false;
                }

                if (!formData.gender) {
                    newErrors.gender = 'Gender is required';
                    isValid = false;
                }
                break;
            
            case 1:
                newErrors.university_id = '';
                newErrors.email = '';
                newErrors.school_id = '';

                if (!formData.university_id) {
                    newErrors.university_id = 'University selection is required';
                    isValid = false;
                }

                if (!formData.email.trim()) {
                    newErrors.email = 'Email is required';
                    isValid = false;
                } else if (!EMAIL_REGEX.test(formData.email)) {
                    newErrors.email = 'Please enter a valid email address';
                    isValid = false;
                }

                if (formData.has_schools && !formData.school_id) {
                    newErrors.school_id = 'Please select a school/department';
                    isValid = false;
                }
                break;
            
            case 2:
                newErrors.password = '';
                newErrors.password_confirmation = '';

                if (!formData.password) {
                    newErrors.password = 'Password is required';
                    isValid = false;
                }

                if (!formData.password_confirmation) {
                    newErrors.password_confirmation = 'Please confirm your password';
                    isValid = false;
                } else if (formData.password !== formData.password_confirmation) {
                    newErrors.password_confirmation = 'Passwords do not match';
                    isValid = false;
                }
                break;
            
            default:
                isValid = false;
        }

        newStepValidations[activeStep] = isValid;
        setStepValidations(newStepValidations);
        setErrors(newErrors);
        return isValid;
    };

    const fetchUniversities = async () => {
        setLoading(true);
        setError(null);

        try {
            const api = authService.getApi();
            const response = await api.get(APIUniversityNames);
            const data = Array.isArray(response.data.data) ? response.data.data : [];
            setUniversities(data);
            showToast("Success", `Loaded ${data.length} universities`, "success");
        } catch (error) {
            let errorMessage = "Failed to load universities";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = "CONNECTION_ERROR";
            }
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const loadUniversitySchools = async (universityId) => {
        setLoadingSchools(true);
        try {
            const api = authService.getApi();
            const response = await api.get(APIUniversitySchools + `/${universityId}`);
            const schools = response.data.data || [];
            setSelectedUniversitySchools(schools);
            
            if (schools.length === 1) {
                setFormData(prev => ({
                    ...prev,
                    school_id: schools[0].id,
                    school_name: schools[0].name
                }));
                showToast("Info", `School auto-selected: ${schools[0].name}`, "info");
            }
        } catch (error) {
            console.error('Error loading schools:', error);
            setSelectedUniversitySchools([]);
            showToast("Error", "Failed to load schools for this university", "error");
        } finally {
            setLoadingSchools(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'contact_phone') {
            const numericValue = value.replace(/\D/g, '').slice(0, 10);
            setFormData(prev => ({ ...prev, [name]: numericValue }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (errors[name] || errors.form) {
            setErrors(prev => ({ ...prev, [name]: '', form: '' }));
        }
    };

    const handleUniversitySelect = (event, selectedUniversity) => {
        if (selectedUniversity) {
            setFormData(prev => ({
                ...prev,
                university_id: selectedUniversity.id,
                university_name: selectedUniversity.name,
                school_id: '',
                school_name: '',
                has_schools: false
            }));
            setSelectedUniversitySchools([]);
            setErrors(prev => ({ ...prev, university_id: '', school_id: '' }));
            showToast("Success", `Selected: ${selectedUniversity.name}`, "success");
        } else {
            setFormData(prev => ({
                ...prev,
                university_id: '',
                university_name: '',
                school_id: '',
                school_name: '',
                has_schools: false
            }));
            setSelectedUniversitySchools([]);
        }
    };

    const handleSchoolSelect = (event, selectedSchool) => {
        if (selectedSchool) {
            setFormData(prev => ({
                ...prev,
                school_id: selectedSchool.id,
                school_name: selectedSchool.name
            }));
            setErrors(prev => ({ ...prev, school_id: '' }));
            showToast("Success", `School selected: ${selectedSchool.name}`, "success");
        } else {
            setFormData(prev => ({
                ...prev,
                school_id: '',
                school_name: ''
            }));
        }
    };

    const handleHasSchoolsToggle = (event) => {
        const checked = event.target.checked;
        setFormData(prev => ({
            ...prev,
            has_schools: checked,
            school_id: checked ? prev.school_id : '',
            school_name: checked ? prev.school_name : ''
        }));
        if (!checked) {
            setErrors(prev => ({ ...prev, school_id: '' }));
        }
    };

    const validateForm = () => {
        let valid = true;
        const newErrors = {
            full_name: '',
            email: '',
            contact_phone: '',
            title: '',
            nationality: '',
            university_id: '',
            school_id: '',
            gender: '',
            password: '',
            password_confirmation: '',
            form: ''
        };

        if (!formData.full_name.trim()) {
            newErrors.full_name = 'Full name is required';
            valid = false;
        }

        if (!formData.contact_phone.trim()) {
            newErrors.contact_phone = 'Contact number is required';
            valid = false;
        } else if (!CONTACT_REGEX.test(formData.contact_phone)) {
            newErrors.contact_phone = 'Contact number must be exactly 10 digits';
            valid = false;
        }

        if (!formData.title) {
            newErrors.title = 'Title is required';
            valid = false;
        }

        if (!formData.nationality.trim()) {
            newErrors.nationality = 'Nationality is required';
            valid = false;
        }

        if (!formData.gender) {
            newErrors.gender = 'Gender is required';
            valid = false;
        }

        if (!formData.university_id) {
            newErrors.university_id = 'University selection is required';
            valid = false;
        }

        if (formData.has_schools && !formData.school_id) {
            newErrors.school_id = 'School/Department selection is required';
            valid = false;
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
            valid = false;
        } else if (!EMAIL_REGEX.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
            valid = false;
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
            valid = false;
        }

        if (!formData.password_confirmation) {
            newErrors.password_confirmation = 'Please confirm your password';
            valid = false;
        } else if (formData.password !== formData.password_confirmation) {
            newErrors.password_confirmation = 'Passwords do not match';
            valid = false;
        }

        if (!valid) {
            newErrors.form = 'Please fix all validation errors before submitting';
        }

        setErrors(newErrors);
        return valid;
    };

    const resetForm = () => {
        setFormData({
            full_name: '',
            email: '',
            contact_phone: '',
            title: '',
            nationality: '',
            university_id: '',
            university_name: '',
            school_id: '',
            school_name: '',
            has_schools: false,
            gender: '',
            password: '',
            password_confirmation: ''
        });
        setActiveStep(0);
        setUniversitySearchInput('');
        setSelectedUniversitySchools([]);
        setErrors({
            full_name: '',
            email: '',
            contact_phone: '',
            title: '',
            nationality: '',
            university_id: '',
            school_id: '',
            gender: '',
            password: '',
            password_confirmation: '',
            form: ''
        });
    };

    const handleNextStep = () => {
        if (validateCurrentStep()) {
            setActiveStep(prev => Math.min(prev + 1, 2));
            showToast("Step Complete", `Step ${activeStep + 1} completed successfully`, "success");
        } else {
            showToast("Validation Error", "Please complete all fields in the current step correctly", "warning");
        }
    };

    const handlePreviousStep = () => {
        setActiveStep(prev => Math.max(prev - 1, 0));
    };
    
    const handleGoBack = () => {
        navigate('/register');
        showToast("Navigation", "Returning to registration options", "info");
    };
    
    const handleLogIn = () => {
        navigate('/');
        showToast("Navigation", "Redirecting to login page", "info");
    };

    const handleRedirectToLogin = () => {
        navigate('/login');
    };

    const handleSubmit = async () => {
        // Prevent concurrent double submissions synchronously
        if (isSubmitting || isSubmittingRef.current) {
            return;
        }
        if (!validateForm()) {
            showToast("Validation Error", "Please fix all validation errors before submitting", "error");
            return;
        }

        setIsSubmitting(true);
        showToast("Processing", "Creating your account...", "info");

        try {
            const registrationData = {
                full_name: formData.full_name,
                email: formData.email,
                title: formData.title,
                university_id: formData.university_id,
                password: formData.password,
                password_confirmation: formData.password_confirmation,
                contact_phone: formData.contact_phone,
                nationality: formData.nationality,
                gender: formData.gender
            };

            if (formData.has_schools && formData.school_id) {
                registrationData.school_id = formData.school_id;
            }

            const response = await authService.registerStudent(registrationData);

            if (response.status === "success") {
                setRegisteredUserData({
                    full_name: formData.full_name,
                    email: formData.email,
                    contact_phone: formData.contact_phone,
                    university_name: formData.university_name,
                    school_name: formData.has_schools ? formData.school_name : null,
                    nationality: formData.nationality,
                    title: formData.title,
                    gender: formData.gender
                });
                
                setShowSuccessPage(true);
                resetForm();

                showToast(
                    "Success", 
                    "Your account has been created and activated successfully!",
                    "success"
                );
            } else {
                showToast("Error", response.message || "Registration failed", "error");
            }
        } catch (error) {
            console.error('Registration error:', error);
            
            if (error.errors) {
                let errorMessages = [];
                for (const field in error.errors) {
                    errorMessages.push(...error.errors[field]);
                }
                showToast("Validation Error", errorMessages.join("\n"), "error");
            } else if (error.message) {
                showToast("Error", error.message, "error");
            } else {
                showToast("Error", "An unexpected error occurred during registration", "error");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const steps = ['Personal Information', 'Academic Details', 'Security Setup'];

    if (showSuccessPage) {
        return (
            <SuccessPage 
                userData={registeredUserData} 
                onRedirect={handleRedirectToLogin}
            />
        );
    }

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="h6" color="primary" sx={{ ml: 2 }}>
                Loading universities...
            </Typography>
        </Box>
    );

    return (
        <Box sx={{background: `${theme.palette.common.auth}`}}>
            <Container maxWidth="md" sx={{ py: 4}}>
                <Zoom in={true}>
                    <ModernCard elevation={0}>
                        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <LogoImage src={logoIcon} alt="SJC ScholarFlow" />
                                <Typography 
                                    variant="h4" 
                                    gutterBottom 
                                    sx={{ 
                                        fontSize: isMobile ? '1.5rem' : '2rem',
                                        background: 'linear-gradient(45deg, #1976d2, #00bcd4)',
                                        backgroundClip: 'text',
                                        textFillColor: 'transparent',
                                        fontWeight: 'bold',
                                        mt: 2
                                    }}
                                >
                                    SJC ScholarFlow Registration
                                </Typography>
                                <Typography variant="subtitle1" color="text.secondary">
                                    Join the premier research workflow and supervision platform
                                </Typography>
                            </Box>

                            <Stepper activeStep={activeStep} sx={{ mb: 4, overflowX: 'auto' }}>
                                {steps.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>

                            {error && (
                                <Alert severity="error" sx={{ mb: 3 }} icon={<Cancel />}>
                                    <Typography variant="subtitle1">Error: {error}</Typography>
                                </Alert>
                            )}

                            <Box component="div">
                                {/* Step 1: Personal Information */}
                                <Fade in={activeStep === 0} timeout={500}>
                                    <Box sx={{ display: activeStep === 0 ? 'grid' : 'none', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 3 }}>
                                        <FormControl fullWidth error={!!errors.title}>
                                            <InputLabel>Title *</InputLabel>
                                            <Select
                                                name="title"
                                                value={formData.title}
                                                onChange={handleInputChange}
                                                label="Title *"
                                            >
                                                <MenuItem value="">Select Title</MenuItem>
                                                <MenuItem value="Mr">Mr</MenuItem>
                                                <MenuItem value="Mrs">Mrs</MenuItem>
                                                <MenuItem value="Ms">Ms</MenuItem>
                                                <MenuItem value="Dr">Dr</MenuItem>
                                                <MenuItem value="Prof">Prof</MenuItem>
                                            </Select>
                                            {errors.title && (
                                                <FormHelperText>{errors.title}</FormHelperText>
                                            )}
                                        </FormControl>

                                        <TextField
                                            name="full_name"
                                            label="Full Name *"
                                            value={formData.full_name}
                                            onChange={handleInputChange}
                                            fullWidth
                                            error={!!errors.full_name}
                                            helperText={errors.full_name}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Person color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        <TextField
                                            name="contact_phone"
                                            label="Contact Phone *"
                                            value={formData.contact_phone}
                                            onChange={handleInputChange}
                                            fullWidth
                                            error={!!errors.contact_phone}
                                            helperText={errors.contact_phone || "Must be exactly 10 digits"}
                                            placeholder="0712345678"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Phone color="action" />
                                                    </InputAdornment>
                                                ),
                                                inputProps: {
                                                    maxLength: 10,
                                                    pattern: "[0-9]*",
                                                    inputMode: "numeric"
                                                }
                                            }}
                                        />

                                        <TextField
                                            name="nationality"
                                            label="Nationality *"
                                            value={formData.nationality}
                                            onChange={handleInputChange}
                                            fullWidth
                                            error={!!errors.nationality}
                                            helperText={errors.nationality}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Flag color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        <FormControl fullWidth error={!!errors.gender}>
                                            <InputLabel>Gender *</InputLabel>
                                            <Select
                                                name="gender"
                                                value={formData.gender}
                                                onChange={handleInputChange}
                                                label="Gender *"
                                            >
                                                <MenuItem value="">Select Gender</MenuItem>
                                                <MenuItem value="male">Male</MenuItem>
                                                <MenuItem value="female">Female</MenuItem>
                                                <MenuItem value="other">Other</MenuItem>
                                            </Select>
                                            {errors.gender && (
                                                <FormHelperText>{errors.gender}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Box>
                                </Fade>

                                {/* Step 2: Academic Details */}
                                <Fade in={activeStep === 1} timeout={500}>
                                    <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
                                        <Box sx={{ mb: 3 }}>
                                            <Autocomplete
                                                id="university-select"
                                                options={universities}
                                                loading={loading}
                                                value={universities.find(u => u.id === formData.university_id) || null}
                                                onChange={handleUniversitySelect}
                                                inputValue={universitySearchInput}
                                                onInputChange={(event, newInputValue) => {
                                                    setUniversitySearchInput(newInputValue);
                                                }}
                                                getOptionLabel={(option) => `${option.name} ${option.code ? `(${option.code})` : ''}`}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                renderOption={(props, option) => (
                                                    <li {...props}>
                                                        <Box>
                                                            <Typography variant="body1">
                                                                <strong>{option.name}</strong>
                                                            </Typography>
                                                            {option.code && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Code: {option.code}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </li>
                                                )}
                                                renderInput={(params) => {
                                                    const { InputLabelProps, InputProps, ...rest } = params;
                                                    return (
                                                        <TextField
                                                            {...rest}
                                                            label="Select University / Institution *"
                                                            required
                                                            error={!!errors.university_id}
                                                            helperText={errors.university_id || "Search and select your university"}
                                                            InputProps={{
                                                                ...InputProps,
                                                                startAdornment: (
                                                                    <>
                                                                        <InputAdornment position="start">
                                                                            <School color="action" />
                                                                        </InputAdornment>
                                                                        {InputProps?.startAdornment}
                                                                    </>
                                                                ),
                                                                endAdornment: (
                                                                    <>
                                                                        {loading && <CircularProgress color="inherit" size={20} />}
                                                                        <Badge 
                                                                            badgeContent={universities.length} 
                                                                            color="primary"
                                                                            sx={{ mr: 1 }}
                                                                        />
                                                                        {InputProps?.endAdornment}
                                                                    </>
                                                                ),
                                                            }}
                                                        />
                                                    );
                                                }}
                                            />
                                        </Box>

                                        {/* Has Schools Toggle */}
                                        {formData.university_id && (
                                            <Box sx={{ mb: 3 }}>
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: 2,
                                                        borderRadius: 2,
                                                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                                        border: '1px solid #e2e8f0'
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={formData.has_schools}
                                                                onChange={handleHasSchoolsToggle}
                                                                color="primary"
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                                    <LocationCity sx={{ mr: 1, verticalAlign: 'middle' }} />
                                                                    Does your institution have schools/departments?
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Enable this if you belong to a specific school within your university
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </Paper>
                                            </Box>
                                        )}

                                        {/* School Selection - Only show if has_schools is true */}
                                        <Collapse in={formData.has_schools && !!formData.university_id}>
                                            <Box sx={{ mb: 3 }}>
                                                <Autocomplete
                                                    id="school-select"
                                                    options={selectedUniversitySchools}
                                                    loading={loadingSchools}
                                                    value={selectedUniversitySchools.find(s => s.id === formData.school_id) || null}
                                                    onChange={handleSchoolSelect}
                                                    getOptionLabel={(option) => option.name}
                                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                                    renderOption={(props, option) => (
                                                        <li {...props}>
                                                            <Box>
                                                                <Typography variant="body1">
                                                                    <Domain sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1rem' }} />
                                                                    <strong>{option.name}</strong>
                                                                </Typography>
                                                                {option.code && (
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        Code: {option.code}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </li>
                                                    )}
                                                    renderInput={(params) => {
                                                        const { InputProps, ...rest } = params;
                                                        return (
                                                            <TextField
                                                                {...rest}
                                                                label="Select School / Department *"
                                                                required
                                                                error={!!errors.school_id}
                                                                helperText={errors.school_id || "Select the school you belong to"}
                                                                InputProps={{
                                                                    ...InputProps,
                                                                    startAdornment: (
                                                                        <>
                                                                            <InputAdornment position="start">
                                                                            <Domain color="action" />
                                                                        </InputAdornment>
                                                                        {InputProps?.startAdornment}
                                                                    </>
                                                                ),
                                                                endAdornment: (
                                                                    <>
                                                                        {loadingSchools && <CircularProgress color="inherit" size={20} />}
                                                                        {selectedUniversitySchools.length > 0 && (
                                                                            <Chip
                                                                                label={`${selectedUniversitySchools.length} schools`}
                                                                                size="small"
                                                                                color="info"
                                                                                sx={{ mr: 1 }}
                                                                            />
                                                                        )}
                                                                        {InputProps?.endAdornment}
                                                                    </>
                                                                ),
                                                            }}
                                                        />
                                                    );
                                                }}
                                                noOptionsText="No schools found for this university"
                                            />
                                            {selectedUniversitySchools.length === 0 && formData.has_schools && (
                                                <Alert severity="info" sx={{ mt: 1 }}>
                                                    No schools are currently configured for this university. 
                                                    You can continue without selecting a school.
                                                </Alert>
                                            )}
                                        </Box>
                                    </Collapse>

                                    <TextField
                                        name="email"
                                        label="Email Address *"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        fullWidth
                                        error={!!errors.email}
                                        helperText={errors.email}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2.5,
                                            mb: 3,
                                            borderRadius: 2,
                                            marginTop: 3,
                                            background: 'linear-gradient(135deg, #f0f7ff 0%, #e3f2fd 100%)',
                                            border: '1px solid',
                                            borderColor: 'primary.100',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                            <CheckCircle color="success" sx={{ mt: 0.5 }} />
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'success.dark', mb: 1 }}>
                                                    <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                                                    No Email Verification Required
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                                    Your account will be automatically activated upon registration. 
                                                    You can log in immediately after completing the registration process.
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Box>
                            </Fade>

                            {/* Step 3: Security Setup */}
                            <Fade in={activeStep === 2} timeout={500}>
                                <Box sx={{ display: activeStep === 2 ? 'grid' : 'none', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 3 }}>
                                    {/* Password Field */}
                                    <TextField
                                        name="password"
                                        label="Password *"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        fullWidth
                                        error={!!errors.password}
                                        helperText={errors.password || "Can be simple: words, letters, or numbers"}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock color="action" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <IconButton
                                                        type="button"
                                                        onClick={() => setShowPassword((prev) => !prev)}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        title={showPassword ? "Hide password" : "Show password"}
                                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                                        sx={{
                                                            color: showPassword ? 'primary.main' : '#757575',
                                                            padding: '8px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            minWidth: '40px',
                                                            minHeight: '40px',
                                                            cursor: 'pointer',
                                                            transition: 'color 0.2s ease',
                                                            '&:hover': {
                                                                color: 'primary.dark'
                                                            }
                                                        }}
                                                    >
                                                        {showPassword ? (
                                                            <VisibilityOff sx={{ fontSize: 24, display: 'block', minWidth: 24, minHeight: 24 }} />
                                                        ) : (
                                                            <Visibility sx={{ fontSize: 24, display: 'block', minWidth: 24, minHeight: 24 }} />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    {/* Confirm Password Field */}
                                    <TextField
                                        name="password_confirmation"
                                        label="Confirm Password *"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={formData.password_confirmation}
                                        onChange={handleInputChange}
                                        fullWidth
                                        error={!!errors.password_confirmation}
                                        helperText={errors.password_confirmation}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock color="action" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <IconButton
                                                        type="button"
                                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        title={showConfirmPassword ? "Hide password" : "Show password"}
                                                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                                        sx={{
                                                            color: showConfirmPassword ? 'primary.main' : '#757575',
                                                            padding: '8px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            minWidth: '40px',
                                                            minHeight: '40px',
                                                            cursor: 'pointer',
                                                            transition: 'color 0.2s ease',
                                                            '&:hover': {
                                                                color: 'primary.dark'
                                                            }
                                                        }}
                                                    >
                                                        {showConfirmPassword ? (
                                                            <VisibilityOff sx={{ fontSize: 24, display: 'block', minWidth: 24, minHeight: 24 }} />
                                                        ) : (
                                                            <Visibility sx={{ fontSize: 24, display: 'block', minWidth: 24, minHeight: 24 }} />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Fade>

                            {errors.form && (
                                <Box sx={{ mt: 2 }}>
                                    <Alert severity="error">{errors.form}</Alert>
                                </Box>
                            )}

                            {/* Bottom Centered Step Navigation Buttons (Back & Next) */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 4, mb: 2 }}>
                                {activeStep > 0 && (
                                    <Button 
                                        variant="outlined"
                                        onClick={handlePreviousStep}
                                        startIcon={<ArrowBack />}
                                        disabled={isSubmitting}
                                        sx={{ minWidth: 130, py: 1.2, borderRadius: 2 }}
                                    >
                                        Back
                                    </Button>
                                )}
                                {activeStep < 2 && (
                                    <Button 
                                        variant="contained"
                                        onClick={handleNextStep}
                                        disabled={!stepValidations[activeStep]}
                                        sx={{ minWidth: 130, py: 1.2, borderRadius: 2 }}
                                    >
                                        Next
                                    </Button>
                                )}
                            </Box>

                            {/* Step 0 Additional Navigation Actions */}
                            {activeStep === 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Divider sx={{ my: 2 }} />
                                    <Grid container spacing={2} justifyContent="center">
                                        <Grid item xs={12} sm={6}>
                                            <ActionButton
                                                fullWidth
                                                variant="outlined"
                                                color="secondary"
                                                startIcon={<ArrowBackIos />}
                                                onClick={handleGoBack}
                                                sx={{
                                                    borderColor: '#FF6B6B',
                                                    color: '#FF6B6B',
                                                    borderRadius: 20,
                                                    '&:hover': {
                                                        borderColor: '#FF6B6B',
                                                        backgroundColor: muiAlpha('#FF6B6B', 0.1),
                                                    }
                                                }}
                                            >
                                                Back to Registration Options
                                            </ActionButton>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <ActionButton
                                                fullWidth
                                                variant="contained"
                                                startIcon={<Login />}
                                                onClick={handleLogIn}
                                                sx={{
                                                    borderRadius: 20,
                                                    background: 'linear-gradient(135deg, #10B981, #059669)',
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #059669, #047857)',
                                                    }
                                                }}
                                            >
                                                Sign In to Existing Account
                                            </ActionButton>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* Step 3 Final Registration Action */}
                            {activeStep === 2 && (
                                <Box sx={{ mt: 3 }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        disabled={isSubmitting || !stepValidations[2]}
                                        fullWidth
                                        onClick={handleSubmit}
                                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <Send />}
                                        sx={{ 
                                            py: 1.5, 
                                            fontSize: '1.1rem',
                                            position: 'relative',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={20} color="inherit" />
                                                Creating Your Account...
                                            </Box>
                                        ) : (
                                            'Register Now'
                                        )}
                                    </Button>

                                    {isSubmitting && (
                                        <Box sx={{ width: '100%', mt: 1 }}>
                                            <LinearProgress
                                                sx={{ 
                                                    height: 4,
                                                    borderRadius: 2
                                                }}
                                            />
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                                                Setting up your research account...
                                            </Typography>
                                        </Box>
                                    )}

                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="large"
                                        fullWidth
                                        sx={{ mt: 2 }}
                                        onClick={() => navigate('/login')}
                                        startIcon={<Login />}
                                        disabled={isSubmitting}
                                    >
                                        Already have an account? Sign In
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </ModernCard>
            </Zoom>
        </Container>
    </Box>
);
};

export default StudentRegistration;